import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';

// Single contact point for any support requests / help links.
const SUPPORT_EMAIL = 'theeloop530@gmail.com';

// Reusable expandable settings row (accordion-style). Replaces the previous
// dead-link <Link to="/"> rows on the SETTINGS card.
const SettingsRow = ({ label, isOpen, onToggle, testId, children, last }) => (
  <div
    style={{
      borderBottom: last ? 'none' : '1px solid #16261C',
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      style={{
        width: '100%',
        background: 'transparent',
        border: 'none',
        color: '#FEFEE3',
        padding: '14px 4px',
        fontSize: '12px',
        letterSpacing: '0.05em',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: 'inherit',
      }}
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        style={{
          color: '#4AE54A',
          fontSize: '14px',
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 150ms ease',
          display: 'inline-block',
        }}
      >
        ›
      </span>
    </button>
    {isOpen && (
      <div
        style={{
          padding: '4px 4px 16px',
          fontSize: '11px',
          color: '#CFCFB8',
          lineHeight: 1.6,
        }}
      >
        {children}
      </div>
    )}
  </div>
);

const Account = ({ deviceId, userName, userAvatar }) => {
  const navigate = useNavigate();

  // Logged-in user (set during onboarding via Onboarding.jsx -> localStorage.loop_user)
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);

  // Store credit balance state
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditError, setCreditError] = useState('');

  // Which settings row is currently expanded (null = all collapsed)
  const [openSection, setOpenSection] = useState(null);
  const toggleSection = (key) => setOpenSection((s) => (s === key ? null : key));

  // Load logged-in user from localStorage and pull credit + profile in parallel.
  const loadAccountData = useCallback(async () => {
    try {
      const stored = localStorage.getItem('loop_user');
      if (!stored) return;
      const parsed = JSON.parse(stored);
      setUser(parsed);

      const email = parsed?.email;
      if (!email) return;

      // Credit and profile both depend on email. Fetch in parallel.
      const [creditResult, profileResult] = await Promise.allSettled([
        api.getProfileCredit(email),
        api.getUserProfile(email),
      ]);

      if (creditResult.status === 'fulfilled') {
        setCreditBalance(
          typeof creditResult.value?.balance === 'number'
            ? creditResult.value.balance
            : 0,
        );
      } else {
        setCreditBalance(0);
        setCreditError('Could not load credit balance');
      }

      if (profileResult.status === 'fulfilled') {
        setProfile(profileResult.value || null);
      }
    } catch (err) {
      console.error('Account load error:', err);
    }
  }, []);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  // Pull saved shipping addresses from the user profile (populated by past orders).
  const savedAddresses = (profile && profile.shipping_addresses) || [];

  // Sign out: clear session + remembered user, send to home.
  const handleSignOut = () => {
    sessionStorage.clear();
    localStorage.removeItem('loop_user');
    navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0D1F1A', color: '#FEFEE3', paddingBottom: '100px' }}>
      <TopNav deviceId={deviceId} currentPath="/account" />

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
        {/* Avatar header */}
        <div style={{ textAlign: 'center', marginBottom: '24px', paddingTop: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '15px' }}>{userAvatar || '⛳'}</div>
          <h2 style={{ fontSize: '20px', color: '#4AE54A', marginBottom: '5px' }}>
            {userName || (user && user.full_name) || 'GUEST'}
          </h2>
          <p style={{ fontSize: '12px', color: '#CFCFB8' }}>LOOP MEMBER</p>
          {user?.email && (
            <p style={{ fontSize: '11px', color: '#7A8A82', marginTop: '6px' }}>{user.email}</p>
          )}
        </div>

        {/* STORE CREDIT card — only visible when we have an email on file. */}
        {user?.email && (
          <div
            data-testid="store-credit-card"
            className="retro-card"
            style={{
              background: '#111A14',
              padding: '20px',
              marginBottom: '20px',
              border: '3px solid #FFC857',
              boxShadow: '4px 4px 0 0 #000',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: '11px', color: '#FFC857', letterSpacing: '0.12em', marginBottom: '6px' }}>
                  ★ STORE CREDIT
                </p>
                <p
                  data-testid="store-credit-balance"
                  style={{ fontSize: '28px', color: '#FEFEE3', fontWeight: 'bold', fontFamily: 'IBM Plex Mono, monospace' }}
                >
                  {creditBalance === null
                    ? '—'
                    : `$${Number(creditBalance).toFixed(2)}`}
                </p>
                {creditError && (
                  <p style={{ fontSize: '10px', color: '#FF5A5F', marginTop: '4px' }}>{creditError}</p>
                )}
              </div>
              <Link
                to="/profile"
                data-testid="view-credit-history-link"
                style={{
                  background: '#FFC857',
                  color: '#000',
                  padding: '10px 14px',
                  fontSize: '10px',
                  textDecoration: 'none',
                  letterSpacing: '0.1em',
                  border: '3px solid #000',
                  boxShadow: '3px 3px 0 0 #000',
                  fontWeight: 'bold',
                }}
              >
                HISTORY ›
              </Link>
            </div>
            <p style={{ fontSize: '10px', color: '#7A8A82', marginTop: '10px' }}>
              Store credit applies automatically at checkout.
            </p>
          </div>
        )}

        {/* QUICK ACTIONS */}
        <div className="retro-card" style={{ background: '#111A14', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', color: '#4AE54A', marginBottom: '15px' }}>QUICK ACTIONS</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Link to="/orders" className="retro-btn" style={{ background: '#1E4B32', color: '#FEFEE3', padding: '15px', textDecoration: 'none', textAlign: 'center', fontSize: '11px' }}>
              📦 ORDERS
            </Link>
            <Link to="/cart" className="retro-btn" style={{ background: '#1E4B32', color: '#FEFEE3', padding: '15px', textDecoration: 'none', textAlign: 'center', fontSize: '11px' }}>
              🛒 CART
            </Link>
            <Link to="/rewards" className="retro-btn" style={{ background: '#1E4B32', color: '#FEFEE3', padding: '15px', textDecoration: 'none', textAlign: 'center', fontSize: '11px' }}>
              ⭐ REWARDS
            </Link>
            <Link to="/" className="retro-btn" style={{ background: '#1E4B32', color: '#FEFEE3', padding: '15px', textDecoration: 'none', textAlign: 'center', fontSize: '11px' }}>
              🏪 SHOP
            </Link>
          </div>
        </div>

        {/* SETTINGS — accordion sections */}
        <div className="retro-card" style={{ background: '#111A14', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', color: '#4AE54A', marginBottom: '8px' }}>SETTINGS</h3>

          {/* ORDER HISTORY: keep behaviour as a real route to the orders page */}
          <Link
            to="/orders"
            data-testid="settings-order-history"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#FEFEE3',
              textDecoration: 'none',
              padding: '14px 4px',
              fontSize: '12px',
              borderBottom: '1px solid #16261C',
              letterSpacing: '0.05em',
            }}
          >
            <span>ORDER HISTORY</span>
            <span aria-hidden="true" style={{ color: '#4AE54A', fontSize: '14px' }}>›</span>
          </Link>

          {/* ADDRESSES */}
          <SettingsRow
            label="ADDRESSES"
            isOpen={openSection === 'addresses'}
            onToggle={() => toggleSection('addresses')}
            testId="settings-addresses"
          >
            {!user?.email && (
              <p>Sign in or place an order to start saving addresses.</p>
            )}
            {user?.email && savedAddresses.length === 0 && (
              <p>No saved addresses yet — your shipping address from your first order will appear here automatically.</p>
            )}
            {user?.email && savedAddresses.length > 0 && (
              <div data-testid="saved-addresses-list">
                {savedAddresses.map((addr, idx) => (
                  <div
                    key={`${addr.address}-${addr.zip_code}-${idx}`}
                    style={{
                      padding: '10px 0',
                      borderBottom: idx < savedAddresses.length - 1 ? '1px dashed #1E4B32' : 'none',
                    }}
                  >
                    {addr.name && (
                      <p style={{ color: '#FEFEE3', marginBottom: '2px' }}>{addr.name}</p>
                    )}
                    <p style={{ color: '#CFCFB8' }}>{addr.address}</p>
                    <p style={{ color: '#7A8A82' }}>
                      {[addr.city, addr.state, addr.zip_code].filter(Boolean).join(', ')}
                    </p>
                    {addr.phone && (
                      <p style={{ color: '#7A8A82', marginTop: '2px' }}>☎ {addr.phone}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SettingsRow>

          {/* PAYMENT METHODS */}
          <SettingsRow
            label="PAYMENT METHODS"
            isOpen={openSection === 'payments'}
            onToggle={() => toggleSection('payments')}
            testId="settings-payment-methods"
          >
            <p style={{ marginBottom: '8px' }}>
              We accept all major cards, Apple Pay, Google Pay, PayPal, and store credit.
            </p>
            <p style={{ marginBottom: '8px' }}>
              For your security, payment methods aren't stored on this site — your card details are
              handled directly by Stripe and PayPal at checkout. If you've used Stripe Link before,
              your saved cards will appear automatically when you check out.
            </p>
            <Link
              to="/cart"
              data-testid="payment-go-to-cart"
              style={{
                display: 'inline-block',
                marginTop: '6px',
                background: '#1E4B32',
                color: '#FEFEE3',
                padding: '10px 14px',
                fontSize: '10px',
                textDecoration: 'none',
                letterSpacing: '0.1em',
                border: '2px solid #4AE54A',
              }}
            >
              GO TO CART ›
            </Link>
          </SettingsRow>

          {/* HELP & SUPPORT */}
          <SettingsRow
            label="HELP & SUPPORT"
            isOpen={openSection === 'help'}
            onToggle={() => toggleSection('help')}
            testId="settings-help-support"
            last
          >
            <p style={{ marginBottom: '10px' }}>
              Need a hand with an order, return, or anything else? Drop us a line and we'll get
              right back to you — usually within 24 hours.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Help%20with%20my%20Loop%20Golf%20order`}
              data-testid="support-email-link"
              style={{
                display: 'inline-block',
                background: '#4AE54A',
                color: '#000',
                padding: '10px 14px',
                fontSize: '11px',
                textDecoration: 'none',
                letterSpacing: '0.1em',
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                fontWeight: 'bold',
                marginRight: '8px',
                marginBottom: '8px',
              }}
            >
              ✉ EMAIL SUPPORT
            </a>
            <Link
              to="/orders"
              data-testid="support-view-orders"
              style={{
                display: 'inline-block',
                background: '#1E4B32',
                color: '#FEFEE3',
                padding: '10px 14px',
                fontSize: '11px',
                textDecoration: 'none',
                letterSpacing: '0.1em',
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000',
                fontWeight: 'bold',
              }}
            >
              MY ORDERS ›
            </Link>
            <p style={{ marginTop: '10px', fontSize: '10px', color: '#7A8A82' }}>
              Or email us directly at <span style={{ color: '#4AE54A' }}>{SUPPORT_EMAIL}</span>
            </p>
          </SettingsRow>
        </div>

        <button
          onClick={handleSignOut}
          data-testid="sign-out-btn"
          className="retro-btn"
          style={{ background: '#FF5A5F', color: '#FFF', padding: '15px', width: '100%', fontSize: '12px' }}
        >
          SIGN OUT
        </button>
      </div>

      <BottomNav currentPath="/account" />
    </div>
  );
};

export default Account;
